/**
 * Renderer Context
 *
 * Provides rendering context to all section components.
 * Manages edit mode state and element selection.
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Recipe, EditableElement, EditableSiteContent } from './types';

// =============================================================================
// TYPES
// =============================================================================

export interface StagedChange {
  oldValue: string | string[];
  newValue: string | string[];
  changedAt: string;
  changedBy?: string;
}

export interface RendererContextValue {
  // Site data
  content: EditableSiteContent | null;
  recipe: Recipe | null;
  websiteId: string;
  businessName: string;

  // Edit mode
  editMode: boolean;
  setEditMode: (enabled: boolean) => void;

  // Element selection
  selectedElement: EditableElement | null;
  selectElement: (element: EditableElement | null) => void;

  // Staged changes
  stagedChanges: Record<string, StagedChange>;
  stageChange: (elementId: string, oldValue: string | string[], newValue: string | string[]) => void;
  revertChange: (elementId: string) => void;
  clearAllChanges: () => void;

  // Get current value (staged or original)
  getValue: (elementId: string, originalValue: string | string[]) => string | string[];

  // Helpers
  currentYear: string;
}

// =============================================================================
// CONTEXT
// =============================================================================

const RendererContext = createContext<RendererContextValue | null>(null);

// =============================================================================
// PROVIDER
// =============================================================================

interface RendererProviderProps {
  children: ReactNode;
  content: EditableSiteContent | null;
  initialEditMode?: boolean;
  initialChanges?: Record<string, StagedChange>;
  onChangeStaged?: (changes: Record<string, StagedChange>) => void;
}

export function RendererProvider({
  children,
  content,
  initialEditMode = false,
  initialChanges = {},
  onChangeStaged,
}: RendererProviderProps) {
  const [editMode, setEditMode] = useState(initialEditMode);
  const [selectedElement, setSelectedElement] = useState<EditableElement | null>(null);
  const [stagedChanges, setStagedChanges] = useState<Record<string, StagedChange>>(initialChanges);

  const selectElement = useCallback((element: EditableElement | null) => {
    setSelectedElement(element);
  }, []);

  const stageChange = useCallback((
    elementId: string,
    oldValue: string | string[],
    newValue: string | string[]
  ) => {
    setStagedChanges(prev => {
      const updated = {
        ...prev,
        [elementId]: {
          oldValue,
          newValue,
          changedAt: new Date().toISOString(),
        },
      };
      onChangeStaged?.(updated);
      return updated;
    });
  }, [onChangeStaged]);

  const revertChange = useCallback((elementId: string) => {
    setStagedChanges(prev => {
      const { [elementId]: _, ...rest } = prev;
      onChangeStaged?.(rest);
      return rest;
    });
  }, [onChangeStaged]);

  const clearAllChanges = useCallback(() => {
    setStagedChanges({});
    onChangeStaged?.({});
  }, [onChangeStaged]);

  const getValue = useCallback((elementId: string, originalValue: string | string[]) => {
    const change = stagedChanges[elementId];
    return change ? change.newValue : originalValue;
  }, [stagedChanges]);

  const value: RendererContextValue = {
    content,
    recipe: content?.recipe ?? null,
    websiteId: content?.websiteId ?? '',
    businessName: content?.businessName ?? '',
    editMode,
    setEditMode,
    selectedElement,
    selectElement,
    stagedChanges,
    stageChange,
    revertChange,
    clearAllChanges,
    getValue,
    currentYear: new Date().getFullYear().toString(),
  };

  return (
    <RendererContext.Provider value={value}>
      {children}
    </RendererContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useRenderer(): RendererContextValue {
  const context = useContext(RendererContext);
  if (!context) {
    throw new Error('useRenderer must be used within a RendererProvider');
  }
  return context;
}

// =============================================================================
// UTILITY HOOK FOR EDITABLE ELEMENTS
// =============================================================================

export function useEditableElement(element: EditableElement) {
  const { editMode, selectElement, getValue, stagedChanges } = useRenderer();

  const currentValue = getValue(element.id, element.value);
  const hasChanges = element.id in stagedChanges;

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (editMode && element.editable) {
      e.preventDefault();
      e.stopPropagation();
      selectElement(element);
    }
  }, [editMode, element, selectElement]);

  return {
    currentValue,
    hasChanges,
    isEditable: editMode && element.editable,
    handleClick,
  };
}
